import { useMemo } from "react"
import { useTranslation } from "providers/i18n"
import {
  Typography,
  CardMedia,
  CardContent,
  Box,
  Stack,
  Card,
  useTheme,
  Chip,
} from "@mui/material"
import { deepmerge } from "@mui/utils"
import clsx from "clsx"

import { getDurationDate } from "utils/date"
import {
  getFeatureDetail,
  IPromotionDetail,
  StatusByDateRange,
} from "utils/feature"
import { getIcon } from "utils/icon"
import { LocationIcon } from "components/icons"

import { LocationInfo } from "components/domains/features/LocationInfo"
import { LeftToRightWrapper } from "components/LeftToRightWrapper"

export const PromotionCard = ({ promotion, dense = false, sx = {} }) => {
  const { t } = useTranslation()
  const {
    name,
    coverImage,
    venueLogo,
    startDate,
    endDate,
    hasLocation,
    isAd,
    status,
  } = getFeatureDetail(promotion) as IPromotionDetail

  const durationText = useMemo(
    () => getDurationDate(startDate, endDate),
    [startDate, endDate]
  )

  const theme = useTheme()
  const { styleOverrides } = theme.components?.[`PromotionCard`] || {}
  const {
    root,
    cardMedia,
    cardContent,
    venueLogoContainer,
    cardContentStack,
    duration,
    cardTitle,
    cardInfoContainer,
    iconContainer,
    durationContainer,
    statusTextContainer,
    statusText,
    chipContainer,
    highlightTextChip,
    locationInfo,
  } = styleOverrides

  const locationIcon = useMemo(
    () =>
      getIcon(theme?.assets?.locationIcon) || (
        <LocationIcon className="PromotionCard-locationInfoIcon" />
      ),
    [theme]
  )

  return (
    <Card
      sx={deepmerge(root, sx)}
      className={clsx(
        "PromotionCard-root",
        dense ? "MuiCardContent-dense" : ""
      )}
    >
      <CardMedia sx={cardMedia} image={coverImage}>
        <Box sx={chipContainer}>
          {isAd && (
            <Chip label={t("Highlight")} size="small" sx={highlightTextChip} />
          )}
        </Box>
        {status === StatusByDateRange.Upcoming && (
          <Box sx={statusTextContainer}>
            <Typography variant="caption" component="p" sx={statusText}>
              {t("Upcoming")}
            </Typography>
          </Box>
        )}
      </CardMedia>
      <CardContent sx={cardContent} className="PromotionCard-cardContent">
        <Stack spacing={1} sx={cardContentStack}>
          {venueLogo && (
            <Box sx={venueLogoContainer}>
              <CardMedia component="img" image={venueLogo} />
            </Box>
          )}
          <Box sx={durationContainer}>
            <LeftToRightWrapper>
              <Typography
                className="PromotionCard-durationText"
                sx={duration}
                variant="subTitle"
                component="p"
              >
                {durationText}
              </Typography>
            </LeftToRightWrapper>
          </Box>
          <Typography
            className="PromotionCard-cardTitleText"
            sx={cardTitle}
            variant="title"
            component="h6"
          >
            {t(name)}
          </Typography>
        </Stack>
        <Stack spacing={1}>
          <Box
            sx={{
              ...cardInfoContainer,
              visibility: hasLocation ? "visible" : "hidden",
            }}
          >
            <Box sx={iconContainer}>{locationIcon}</Box>
            <LocationInfo
              feature={promotion}
              variant="subTitle"
              component="p"
              className="PromotionCard-locationInfo"
              sx={locationInfo}
            />
          </Box>
        </Stack>
      </CardContent>
    </Card>
  )
}
